import {
  DAY_SLOTS,
  fmtDayLongYear,
  fmtFull,
  fmtTime,
  utcToWall,
  wallToUtc,
  zoneAbbr,
  zoneName,
} from '@concord/domain';
import type { CaseDetailView, InvitationView } from '@concord/domain/contracts';
import {
  Banner,
  Button,
  Dialog,
  Field,
  Icon,
  Menu,
  Notice,
  Panel,
  PanelHead,
  Pill,
  Schedule,
  ScheduleBig,
  ScheduleNote,
  ScheduleRow,
  useToast,
} from '@concord/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { acceptedLabel, shortTime } from './accepted';
import { caseKey } from './CasePage';
import { TermsLink } from './TermsLink';

export function useCaseAction(c: CaseDetailView) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (a: { path: string; method?: string; body?: unknown; done?: string }) =>
      api(`/staff/cases/${c.reference}${a.path}`, { method: a.method ?? 'POST', body: a.body ?? {} }).then(
        () => a.done,
      ),
    onSuccess: async (done) => {
      await qc.invalidateQueries({ queryKey: caseKey(c.reference ?? '') });
      if (done) toast(done);
    },
    onError: (e) => toast(errorMessage(e), 'bad'),
  });
}

/** The state banner: what is happening, why, and the action that moves it (CON-08). */
export function StateBanner({ c, tz, onSetTime }: { c: CaseDetailView; tz: string; onSetTime: () => void }) {
  const navigate = useNavigate();
  const act = useCaseAction(c);
  const panelLink = `/console/cases/${c.reference}/panel`;
  const k = c.consultation;
  const invited = c.invitations.filter((i) => i.state !== 'staged');
  const accepted = invited.filter((i) => i.state === 'accepted');
  const alt = invited.filter((i) => i.state === 'alternative_requested');
  const open = invited.filter((i) => i.state === 'invited');

  if (c.state === 'draft') {
    return (
      <Banner
        tone="wait"
        title="Intake in progress"
        detail="The patient has not sent this case yet. Nothing goes to physicians until they do."
      />
    );
  }
  if (c.state === 'submitted' || c.state === 'on_hold') {
    const ready = c.blockers.length === 0;
    return (
      <Banner
        tone="hold"
        title={
          c.state === 'on_hold'
            ? `On hold: ${c.holdReason ?? 'no reason given'}`
            : ready
              ? 'Ready to release'
              : 'Not yet released'
        }
        detail={
          ready
            ? 'The entitlement chain is valid, times are offered and the panel is staged. Release sends the invitations.'
            : c.blockers.map((b) => b.message).join(' ')
        }
        actions={
          <Button onClick={() => navigate(panelLink)}>
            {ready ? 'Review and release' : 'Build the panel'}
          </Button>
        }
      />
    );
  }
  if (c.state === 'released' && k?.state === 'provisional') {
    const declined = invited.filter((i) => i.state === 'declined').length;
    const noneLeft = open.length === 0;
    return (
      <Banner
        tone="wait"
        title={`Panel is short: ${accepted.length} of ${c.panelSize} accepted${declined ? `, ${declined} declined` : ''}${noneLeft ? ', and no invitations are open.' : `, ${open.length} still open.`}`}
        detail={`${accepted[0]?.expertName ?? 'A physician'} accepted ${fmtFull(new Date(k.startsAt), tz)}. The time stays provisional until you confirm it or the panel fills. Reducing the panel size to ${accepted.length} confirms it now; adding a physician outside the plan's network needs an out-of-plan override, which is logged.`}
        actions={
          <>
            <Button variant="sec" onClick={() => navigate(panelLink)}>
              Add from outside the network
            </Button>
            <Button
              variant="sec"
              busy={act.isPending}
              onClick={() =>
                act.mutate({
                  path: '/panel',
                  method: 'PATCH',
                  body: {
                    panelSize: accepted.length,
                    expectedResponses: Math.max(accepted.length, c.expectedResponses),
                  },
                  done: 'Panel reduced; time confirmed.',
                })
              }
            >
              Reduce panel to {accepted.length}
            </Button>
            <Button
              busy={act.isPending}
              onClick={() =>
                act.mutate({
                  path: '/consultation/confirm',
                  done: 'Time confirmed. Everyone has been emailed.',
                })
              }
            >
              Confirm {shortTime(k.startsAt, tz)}
            </Button>
          </>
        }
      />
    );
  }
  if (c.state === 'released') {
    if (alt.length) {
      return (
        <Banner
          tone="hold"
          title={`${alt[0]!.expertName} asked for another time`}
          detail={`“${alt[0]!.alternativeText}” None of the offered times work for them. Set a time directly, or reset their invitation after asking the patient for more times.`}
          actions={<Button onClick={onSetTime}>Set a time</Button>}
        />
      );
    }
    const expires = open[0]?.expiresAt;
    return (
      <Banner
        tone="wait"
        title={`Awaiting panel: ${invited.length} invited, ${invited.length - open.length} replied`}
        detail={
          expires
            ? `Open invitations expire ${fmtFull(new Date(expires), tz)}. You can remind a physician or reset their clock from the participants list.`
            : 'Add physicians to invite from the panel.'
        }
        actions={
          <Button variant="sec" onClick={() => navigate(panelLink)}>
            Add physicians
          </Button>
        }
      />
    );
  }
  if (
    c.state === 'scheduled' &&
    k &&
    new Date(k.startsAt).getTime() + k.durationMinutes * 60_000 < Date.now()
  ) {
    return (
      <Banner
        tone="hold"
        title="The consultation time has passed"
        detail={`It was set for ${fmtFull(new Date(k.startsAt), tz)}. If it took place, mark it as held; if not, set a different time.`}
      />
    );
  }
  if (c.state === 'session_complete') {
    return (
      <Banner
        tone="ok"
        title="Consultation held"
        detail="Check in with the patient, then close the case. Consultation minutes are recorded outside the platform until Phase 3."
      />
    );
  }
  if (c.state === 'closed') {
    return (
      <Banner
        tone="ok"
        title="Closed"
        detail={`Closed ${c.closedAt ? fmtFull(new Date(c.closedAt), tz) : ''}. The case is read-only; internal notes can still be added.`}
      />
    );
  }
  if (c.state === 'canceled') {
    return (
      <Banner
        tone="bad"
        title="Canceled"
        detail={`Reason: ${c.canceledReason ?? 'none given'}. The case is read-only.`}
      />
    );
  }
  if (c.state === 'scheduled' && k) {
    return (
      <Banner
        tone="ok"
        title={`Scheduled: ${fmtFull(new Date(k.startsAt), tz)}`}
        detail={`${accepted.map((a) => a.expertName).join(' and ')} ${accepted.length === 1 ? 'has' : 'have'} accepted. Every participant has the time and a calendar file.`}
        actions={
          <Button variant="sec" onClick={onSetTime}>
            Reschedule
          </Button>
        }
      />
    );
  }
  return null;
}

/** "81234567890" as Zoom shows it: "812 3456 7890". */
function meetingNumber(id: string): string {
  return id.length === 11 ? `${id.slice(0, 3)} ${id.slice(3, 7)} ${id.slice(7)}` : id;
}

/** Where the consultation's Zoom meeting stands (ZM-02). */
function ZoomStatus({
  zoom,
  joinUrl,
  retry,
  busy,
}: {
  zoom: NonNullable<NonNullable<CaseDetailView['consultation']>['zoom']>;
  joinUrl: string | null;
  retry: () => void;
  busy: boolean;
}) {
  if (zoom.status === 'ready') {
    return (
      <ScheduleNote>
        Zoom meeting {meetingNumber(zoom.meetingId ?? '')}
        {joinUrl && (
          <>
            {' · '}
            <a className="link" href={joinUrl} target="_blank" rel="noopener noreferrer">
              Open in Zoom
            </a>
          </>
        )}
      </ScheduleNote>
    );
  }
  if (zoom.status === 'creating') {
    return (
      <ScheduleNote>
        Making the Zoom meeting. Its link goes to everyone as soon as Zoom answers
        {zoom.error ? `; the last try failed (${zoom.error}) and Concord is retrying` : ''}.
      </ScheduleNote>
    );
  }
  return (
    <Notice tone="bad">
      Zoom could not make the meeting after several tries ({zoom.error}). Try again, or paste a Zoom link
      below.{' '}
      <Button variant="sec" busy={busy} onClick={retry}>
        Try Zoom again
      </Button>
    </Notice>
  );
}

/** The consultation panel on the case screen (SCH-07 to SCH-10). */
export function ConsultationPanel({
  c,
  tz,
  onSetTime,
}: {
  c: CaseDetailView;
  tz: string;
  onSetTime: () => void;
}) {
  const act = useCaseAction(c);
  const [joinUrl, setJoinUrl] = useState(c.consultation?.joinUrl ?? '');
  const k = c.consultation;
  if (!k) {
    return (
      <Panel aria-label="Consultation">
        <PanelHead title="Consultation" sub="Not scheduled" />
        <Schedule>
          <ScheduleNote>
            A time is held when a physician accepts one of the times the patient offered. You can also set one
            directly.
          </ScheduleNote>
          {(c.state === 'released' || c.state === 'scheduled') && (
            <ScheduleRow>
              <Button variant="sec" onClick={onSetTime}>
                Set a time
              </Button>
            </ScheduleRow>
          )}
        </Schedule>
      </Panel>
    );
  }
  const start = new Date(k.startsAt);
  const accepted = c.invitations.filter((i) => i.state === 'accepted');
  const pz = c.patient.timezone;
  const sees = [
    `Patient sees ${fmtTime(start, pz)} ${zoneName(pz, start).replace(' Time', '')}`,
    ...accepted.map(
      (a) =>
        `${a.expertName.replace(/^Dr\. \S+ /, 'Dr. ')} sees ${fmtTime(start, a.timezone)} ${zoneName(a.timezone, start).replace(' Time', '')}`,
    ),
  ];
  return (
    <Panel aria-label="Consultation">
      <PanelHead title="Consultation">
        <Pill tone={k.state === 'confirmed' ? 'ok' : 'hold'}>
          {k.state === 'confirmed' ? 'Confirmed' : 'Provisional'}
        </Pill>
      </PanelHead>
      <Schedule>
        <ScheduleBig>
          {fmtDayLongYear(start, tz)} · {fmtTime(start, tz)} {zoneAbbr(tz, start)}
        </ScheduleBig>
        <ScheduleNote>
          {sees.join(' · ')} · {k.durationMinutes} minutes
        </ScheduleNote>
        {c.state !== 'closed' && c.state !== 'canceled' && c.state !== 'session_complete' && (
          <ScheduleRow>
            {k.state === 'provisional' && (
              <Button
                busy={act.isPending}
                onClick={() =>
                  act.mutate({
                    path: '/consultation/confirm',
                    done: 'Time confirmed. Everyone has been emailed.',
                  })
                }
              >
                Confirm time
              </Button>
            )}
            <Button variant="sec" onClick={onSetTime}>
              Set a different time
            </Button>
          </ScheduleRow>
        )}
        {k.zoom && c.state !== 'closed' && c.state !== 'canceled' && (
          <ZoomStatus
            zoom={k.zoom}
            joinUrl={k.joinUrl}
            retry={() => act.mutate({ path: '/consultation/zoom/retry', done: 'Zoom tried again.' })}
            busy={act.isPending}
          />
        )}
        {k.state === 'confirmed' && c.state === 'scheduled' && (
          <form
            style={{ display: 'flex', gap: '.5rem' }}
            onSubmit={(e) => {
              e.preventDefault();
              act.mutate({
                path: '/consultation/join-url',
                method: 'PUT',
                body: { url: joinUrl },
                done: 'Join link saved.',
              });
            }}
          >
            <input
              className="input"
              aria-label="Join link"
              placeholder="Paste a Zoom link to use instead"
              value={joinUrl}
              onChange={(e) => setJoinUrl(e.target.value)}
            />
            <Button type="submit" variant="sec" disabled={!joinUrl || joinUrl === k.joinUrl}>
              Save link
            </Button>
          </form>
        )}
        <ScheduleNote>
          Concord makes the Zoom meeting and sends its link to the patient, the physicians and guests with
          every confirmation and reminder; a time change moves the meeting. Paste a link only to use a
          different one. Everyone can join up to 15 minutes early, without a host. Every participant gets a
          calendar file when the time is confirmed.
        </ScheduleNote>
      </Schedule>
    </Panel>
  );
}

const STATUS: Record<
  string,
  {
    tone: 'ok' | 'bad' | 'wait' | 'idle' | 'hold';
    label: (i: InvitationView, tz: string, consultationStartsAt: string | null) => string;
  }
> = {
  staged: { tone: 'idle', label: () => 'Not yet issued' },
  invited: {
    tone: 'wait',
    label: (i, tz) => (i.expiresAt ? `Invited · expires ${fmtTime(new Date(i.expiresAt), tz)}` : 'Invited'),
  },
  accepted: {
    tone: 'ok',
    label: (i, tz, at) => acceptedLabel(i.acceptedStartsAt, at, tz),
  },
  declined: { tone: 'bad', label: () => 'Declined' },
  alternative_requested: { tone: 'hold', label: () => 'Asked for another time' },
  missed_out: { tone: 'idle', label: () => 'Missed out' },
  expired: { tone: 'hold', label: () => 'Expired' },
  withdrawn: { tone: 'idle', label: () => 'Withdrawn' },
};

/** Status pill and per-physician actions for the participants list (MAT-15, MAT-18, MAT-19). */
export function InvitationControls({
  c,
  i,
  tz,
}: {
  c: CaseDetailView;
  i: InvitationView;
  tz: string;
}): ReactNode {
  const act = useCaseAction(c);
  const st = STATUS[i.state] ?? STATUS.invited!;
  const actions = [
    ...(i.state === 'invited'
      ? [
          {
            label: 'Send a reminder',
            onSelect: () =>
              act.mutate({ path: `/invitations/${i.id}/remind`, done: `Reminder sent to ${i.expertName}.` }),
          },
        ]
      : []),
    ...(['invited', 'expired', 'alternative_requested'].includes(i.state)
      ? [
          {
            label: 'Reset the 24-hour clock',
            onSelect: () =>
              act.mutate({
                path: `/invitations/${i.id}/reset`,
                done: `${i.expertName} has a fresh 24 hours.`,
              }),
          },
        ]
      : []),
    ...(['staged', 'invited', 'expired', 'alternative_requested'].includes(i.state)
      ? [
          {
            label: i.state === 'staged' ? 'Remove from panel' : 'Withdraw invitation',
            danger: true,
            separatorBefore: true,
            onSelect: () =>
              act.mutate({
                path: `/invitations/${i.id}/withdraw`,
                body: { reason: null },
                done: `${i.expertName} removed.`,
              }),
          },
        ]
      : []),
  ];
  return (
    <span style={{ display: 'inline-flex', gap: '.35rem', alignItems: 'center' }}>
      <Pill tone={st.tone}>{st.label(i, tz, c.consultation?.startsAt ?? null)}</Pill>
      {i.state === 'accepted' && i.engagementTermsVersion && (
        <span className="sub2">
          terms{' '}
          <TermsLink
            kind="engagement"
            version={i.engagementTermsVersion}
            who={i.expertName}
            acceptedAt={i.respondedAt ?? null}
            tz={tz}
          />
        </span>
      )}
      {actions.length > 0 && (
        <Menu
          trigger={
            <button
              type="button"
              className="btn ghost"
              aria-label={`Actions for ${i.expertName}`}
              style={{ padding: '.2rem .35rem' }}
            >
              <Icon.ChevronDown />
            </button>
          }
          actions={actions}
        />
      )}
    </span>
  );
}

/** SCH-07: set or override the time, in the navigator's zone, at 30-minute steps. */
export function SetTimeDialog({
  c,
  tz,
  open,
  onOpenChange,
}: {
  c: CaseDetailView;
  tz: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const act = useCaseAction(c);
  const initial = c.consultation
    ? utcToWall(new Date(c.consultation.startsAt), tz)
    : { date: '', time: '10:00' };
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [joinUrl, setJoinUrl] = useState(
    c.consultation?.provider === 'external' ? (c.consultation.joinUrl ?? '') : '',
  );
  const moving = Boolean(c.consultation);
  const submit = () => {
    const startsAt = wallToUtc(date, time, tz).toISOString();
    act.mutate(
      {
        path: '/consultation',
        method: 'PUT',
        body: { startsAt, joinUrl: joinUrl.trim() || null },
        done: moving
          ? 'Moved. Everyone has been emailed the new time.'
          : 'Time set. Everyone has been emailed.',
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={moving ? 'Set a different time' : 'Set the consultation time'}
      description={
        moving && c.consultation
          ? `Now ${fmtFull(new Date(c.consultation.startsAt), tz)}. Both times are recorded on the case and every participant is emailed.`
          : 'Every participant is emailed with a calendar file.'
      }
      footer={
        <>
          <Button variant="sec" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!date} busy={act.isPending}>
            {moving ? 'Move the consultation' : 'Set the time'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.9rem' }}>
        <Field label="Date">
          {(id) => (
            <input
              id={id}
              type="date"
              className="input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          )}
        </Field>
        <Field label={`Time (${zoneAbbr(tz)})`}>
          {(id) => (
            <select id={id} className="select" value={time} onChange={(e) => setTime(e.target.value)}>
              {DAY_SLOTS.map((s) => (
                <option key={s.time} value={s.time}>
                  {s.label}
                </option>
              ))}
            </select>
          )}
        </Field>
      </div>
      <Field
        label="Join link"
        hint="Leave empty and Concord makes the Zoom meeting. Paste a Zoom link only to use a meeting of your own."
      >
        {(id, d) => (
          <input
            id={id}
            className="input"
            aria-describedby={d}
            value={joinUrl}
            onChange={(e) => setJoinUrl(e.target.value)}
            placeholder="https://…"
          />
        )}
      </Field>
      {act.isError && <Notice tone="bad">{errorMessage(act.error)}</Notice>}
    </Dialog>
  );
}

/** Guests: invited when the time is confirmed; removable while the case is open (CON-15). */
export function GuestControls({ c, guestId }: { c: CaseDetailView; guestId: string }) {
  const act = useCaseAction(c);
  return (
    <span style={{ display: 'inline-flex', gap: '.35rem', alignItems: 'center' }}>
      <Pill tone={c.consultation?.state === 'confirmed' ? 'ok' : 'idle'}>
        {c.consultation?.state === 'confirmed' ? 'Invited' : 'Invited on confirm'}
      </Pill>
      {!c.readOnly && (
        <Button
          variant="ghost"
          style={{ fontSize: '.76rem' }}
          busy={act.isPending}
          onClick={() => act.mutate({ path: `/guests/${guestId}`, method: 'DELETE', done: 'Guest removed.' })}
        >
          Remove
        </Button>
      )}
    </span>
  );
}
